/**
 * 产品向导：新建 / 编辑产品的全表单流程，客户界面上不出现任何 JSON。
 * 步骤：① 基本信息 → ② 价格档位 → ③ 附加费用 → ④ 确认保存
 */
import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import {
  Alert, Button, Card, CardFooter, Field, Input, Select, Spinner,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow, toast,
} from "@/components/ui";
import * as api from "../api";
import type { Product } from "../api";
import {
  WizardDraft, TierRow, Measure, emptyDraft, emptyTier,
  AREA_UNITS, LENGTH_UNITS, tierLabel, validateDraft,
  draftToProduct, draftToRules, draftToModifiers, productToDraft, preservedModifiers,
} from "./serializers";
import { ModifierHelp, ModifierSandbox } from "./ModifierTestPanel";

const STEPS = ["基本信息", "价格档位", "附加费用", "确认保存"] as const;

interface Props {
  product?: Product;           // 传入 = 编辑模式
  products?: Product[];        // 供"复制现有产品"选择
  scope?: string;
  initialDraft?: WizardDraft;  // AI 解析结果预填
  aiNotes?: string[];          // AI 觉得需要人工确认的地方
  onClose: () => void;
  onBack?: () => void;         // 第一步"上一步"的去处，不传则等同取消
}

export function ProductWizard({ product, products, scope, initialDraft, aiNotes, onClose, onBack }: Props) {
  const qc = useQueryClient();
  const isEdit = !!product;
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<WizardDraft>(() => initialDraft ?? emptyDraft("area"));
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // 编辑模式：加载现有规则/修饰器反解为表单
  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    (async () => {
      try {
        const [rules, mods] = await Promise.all([
          api.listRules(product.id, scope),
          api.listModifiers(product.id, scope),
        ]);
        if (!cancelled) setDraft(productToDraft(product, rules.data, mods.data));
      } catch {
        if (!cancelled) setErr("加载产品数据失败");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [product, scope]);

  const set = (patch: Partial<WizardDraft>) => setDraft((d) => ({ ...d, ...patch }));

  const cloneFrom = async (src: Product) => {
    setLoading(true);
    setErr(null);
    try {
      const [rules, mods] = await Promise.all([
        api.listRules(src.id, scope),
        api.listModifiers(src.id, scope),
      ]);
      const cloned = productToDraft(src, rules.data, mods.data);
      // 保留用户已填的标识/名称/关键词
      setDraft((d) => ({ ...cloned, key: d.key, name: d.name, keywords: d.keywords, sortOrder: d.sortOrder }));
    } catch {
      setErr("复制产品配置失败");
    } finally {
      setLoading(false);
    }
  };

  const next = () => {
    setErr(null);
    if (step === 0) {
      const e = validateDraft({ ...draft, tiers: [emptyTier("perSheet")], specs: draft.specs }, isEdit);
      // 第一步只校验基本信息
      if (e && (e.includes("标识") || e.includes("名称"))) { setErr(e); return; }
    }
    if (step === 1) {
      const e = validateDraft(draft, isEdit);
      if (e && !e.includes("费") && !e.includes("倍数") && !e.includes("起价") && !e.includes("小批量")) { setErr(e); return; }
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const save = async () => {
    setErr(null);
    const e = validateDraft(draft, isEdit);
    if (e) { setErr(e); return; }
    setSaving(true);
    try {
      const payload = draftToProduct(draft, isEdit ? product!.calc_strategy : undefined);
      const rules = draftToRules(draft);
      const modifiers = draftToModifiers(draft);
      if (isEdit) {
        await api.updateProduct(product!.id, {
          name: payload.name, schema_json: payload.schema_json, sort_order: payload.sort_order,
        }, scope);
        await api.replaceRules(product!.id, rules, scope);
        await api.replaceModifiers(product!.id, modifiers, scope);
      } else {
        const created = await api.createProduct(payload, scope);
        try {
          await api.replaceRules(created.id, rules, scope);
          await api.replaceModifiers(created.id, modifiers, scope);
        } catch (inner) {
          // 规则写入失败时回滚产品，避免留下半成品
          await api.deleteProduct(created.id, scope).catch(() => undefined);
          throw inner;
        }
      }
      qc.invalidateQueries({ queryKey: ["admin"] });
      toast.success(isEdit ? "已保存修改" : `已创建产品「${payload.name}」`);
      onClose();
    } catch (e2) {
      setErr(e2 instanceof ApiError
        ? ((e2.body as { detail?: string } | null)?.detail || `保存失败 (${e2.status})`)
        : "保存失败");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="overflow-hidden">
      {/* 步骤条 */}
      <ol className="flex items-center gap-2 overflow-x-auto border-b bg-muted/40 px-4 py-3.5 sm:px-5">
        {STEPS.map((s, i) => (
          <li key={s} className="flex flex-1 items-center gap-2 last:flex-none">
            <div className="flex items-center gap-2 whitespace-nowrap" aria-current={i === step ? "step" : undefined}>
              <span className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium transition-colors",
                i === step ? "bg-primary text-primary-foreground"
                  : i < step ? "bg-primary-soft text-primary ring-1 ring-brand/40"
                  : "bg-card text-muted-foreground ring-1 ring-border",
              )}>
                {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className={cn("text-sm", i === step ? "font-medium text-foreground" : "hidden text-muted-foreground sm:inline")}>{s}</span>
            </div>
            {i < STEPS.length - 1 && <div className={cn("h-px min-w-3 flex-1", i < step ? "bg-brand/40" : "bg-border")} />}
          </li>
        ))}
      </ol>

      <div className="p-4 sm:p-5">
        {aiNotes && aiNotes.length > 0 && step === 0 && (
          <Alert variant="warning" title="AI 已填好表单，这几处请你确认" className="mb-5">
            <ul className="mt-1 list-inside list-disc space-y-0.5">
              {aiNotes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </Alert>
        )}
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Spinner /> 加载中…
          </div>
        ) : (
          <>
            {step === 0 && <StepBasics draft={draft} set={set} isEdit={isEdit} products={products} onClone={cloneFrom} />}
            {step === 1 && <StepTiers draft={draft} set={set} />}
            {step === 2 && <StepModifiers draft={draft} set={set} />}
            {step === 3 && <StepReview draft={draft} />}
          </>
        )}

        {err && <Alert className="mt-4">{err}</Alert>}
      </div>

      <CardFooter className="justify-between bg-muted/40">
        <Button variant="outline" icon={<ArrowLeft />}
          onClick={() => (step === 0 ? (onBack ?? onClose)() : setStep((s) => s - 1))}>
          {step === 0 ? (onBack ? "重新描述" : "取消") : "上一步"}
        </Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={next}>
            下一步 <ArrowRight />
          </Button>
        ) : (
          <Button onClick={save} loading={saving} icon={<Check />}>
            {saving ? "保存中…" : isEdit ? "保存修改" : "创建产品"}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

// ── ① 基本信息 ───────────────────────────────────────────────────

function StepBasics({ draft, set, isEdit, products, onClone }: {
  draft: WizardDraft;
  set: (p: Partial<WizardDraft>) => void;
  isEdit: boolean;
  products?: Product[];
  onClone: (p: Product) => void;
}) {
  const setMeasure = (measure: Measure) => {
    set({
      measure,
      twoLevel: measure === "length" ? draft.twoLevel : false,
      tiers: [emptyTier(measure === "area" ? "perSheet" : "perCm")],
    });
  };

  const measureCard = (value: Measure, title: string, desc: string) => (
    <button
      type="button"
      aria-pressed={draft.measure === value}
      onClick={() => setMeasure(value)}
      className={cn(
        "rounded-lg border p-3 text-left transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        draft.measure === value ? "border-brand bg-primary-soft ring-1 ring-brand" : "bg-card hover:bg-muted",
      )}
    >
      <div className={cn("font-medium", draft.measure === value ? "text-primary" : "text-foreground")}>{title}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{desc}</div>
    </button>
  );

  return (
    <div className="space-y-4 text-sm">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="产品名称" hint="客户报价时看到的名字">
          <Input value={draft.name} onChange={(e) => set({ name: e.target.value })} placeholder="如：亚克力板" />
        </Field>
        <Field label="产品标识" hint="英文小写，创建后不可改">
          <Input value={draft.key} onChange={(e) => set({ key: e.target.value.toLowerCase() })}
            disabled={isEdit} placeholder="如：acrylic" className="font-mono" />
        </Field>
      </div>
      <Field label="识别关键词" hint="客户消息里出现这些词就认成本产品，逗号分隔">
        <Input value={draft.keywords} onChange={(e) => set({ keywords: e.target.value })}
          placeholder="如：亚克力, 阿克力, 亚克力板" />
      </Field>

      <div className="space-y-1.5">
        <div className="text-sm font-medium text-foreground">计价方式</div>
        <div className="grid gap-2 sm:grid-cols-2">
          {measureCard("area", "按面积计价", "客户报 宽×高（米），按面积分档定价。适合板材、画面、贴膜等。")}
          {measureCard("length", "按尺寸计价（如立体字）", "客户报 最长边（厘米），按尺寸分档定价，可再按规格（如厚度）细分。")}
        </div>
      </div>

      {draft.measure === "length" && (
        <label className="flex items-center gap-2">
          <input type="checkbox" className="h-4 w-4 accent-primary" checked={draft.twoLevel} onChange={(e) => set({ twoLevel: e.target.checked })} />
          <span>先按规格（如厚度）细分，每个规格有自己的价格档位</span>
        </label>
      )}

      <div className="grid items-end gap-4 sm:grid-cols-[1fr_8rem]">
        {!isEdit && products && products.length > 0 && (
          <Field label="从现有产品复制价格设置" hint="可选">
            <Select defaultValue="" onChange={(e) => {
              const p = products.find((x) => String(x.id) === e.target.value);
              if (p) onClone(p);
            }}>
              <option value="">不复制，从头开始</option>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
        )}
        <Field label="显示顺序" className="w-32">
          <Input type="number" value={draft.sortOrder}
            onChange={(e) => set({ sortOrder: Number(e.target.value) || 0 })} className="tabular-nums" />
        </Field>
      </div>
    </div>
  );
}

// ── ② 价格档位 ───────────────────────────────────────────────────

function TierTable({ tiers, measure, onChange }: {
  tiers: TierRow[];
  measure: Measure;
  onChange: (tiers: TierRow[]) => void;
}) {
  const units = measure === "area" ? AREA_UNITS : LENGTH_UNITS;
  const unitText = measure === "area" ? "㎡" : "cm";
  const upd = (i: number, patch: Partial<TierRow>) =>
    onChange(tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)));

  const cell = "h-8 px-2";
  return (
    <div className="space-y-2">
      <Table className="min-w-[36rem]">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-28">从（{unitText}）</TableHead>
            <TableHead className="w-28">到（{unitText}）</TableHead>
            <TableHead className="w-16 text-center">含上限</TableHead>
            <TableHead className="w-28">单价（元）</TableHead>
            <TableHead>计价方式</TableHead>
            <TableHead className="w-10"><span className="sr-only">操作</span></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tiers.map((t, i) => (
            <TableRow key={i} className="hover:bg-transparent">
              <TableCell className="py-1.5 pr-1">
                <Input value={t.min} onChange={(e) => upd(i, { min: e.target.value })} placeholder="0"
                  aria-label={`第 ${i + 1} 档 从`} className={cn(cell, "tabular-nums")} />
              </TableCell>
              <TableCell className="py-1.5 pr-1">
                <Input value={t.max} onChange={(e) => upd(i, { max: e.target.value })}
                  placeholder={i === tiers.length - 1 ? "不封顶" : ""}
                  aria-label={`第 ${i + 1} 档 到`} className={cn(cell, "tabular-nums")} />
              </TableCell>
              <TableCell className="py-1.5 text-center">
                <input type="checkbox" className="h-4 w-4 accent-primary" checked={t.inclusive} disabled={t.max.trim() === ""}
                  aria-label={`第 ${i + 1} 档 含上限`}
                  onChange={(e) => upd(i, { inclusive: e.target.checked })} />
              </TableCell>
              <TableCell className="py-1.5 pr-1">
                <Input value={t.price} onChange={(e) => upd(i, { price: e.target.value })}
                  aria-label={`第 ${i + 1} 档 单价`} className={cn(cell, "tabular-nums")} />
              </TableCell>
              <TableCell className="py-1.5 pr-1">
                <Select value={t.unit} onChange={(e) => upd(i, { unit: e.target.value as TierRow["unit"] })}
                  aria-label={`第 ${i + 1} 档 计价方式`} className="[&_select]:h-8">
                  {units.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                </Select>
              </TableCell>
              <TableCell className="py-1.5 text-right">
                <Button variant="ghost" size="icon-sm" onClick={() => onChange(tiers.filter((_, j) => j !== i))}
                  disabled={tiers.length <= 1} aria-label="删除档位"
                  className="text-muted-foreground hover:bg-danger-soft hover:text-danger-strong">
                  <Trash2 />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Button variant="ghost" size="sm" icon={<Plus />} className="text-primary hover:bg-primary-soft"
        onClick={() => {
          const last = tiers[tiers.length - 1];
          onChange([...tiers, { ...emptyTier(last?.unit ?? (measure === "area" ? "perSheet" : "perCm")), min: last?.max ?? "" }]);
        }}>
        添加档位
      </Button>
    </div>
  );
}

function StepTiers({ draft, set }: { draft: WizardDraft; set: (p: Partial<WizardDraft>) => void }) {
  if (draft.measure === "length" && draft.twoLevel) {
    const upd = (i: number, patch: Partial<WizardDraft["specs"][number]>) =>
      set({ specs: draft.specs.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
    return (
      <div className="space-y-4 text-sm">
        <p className="text-xs text-muted-foreground">
          每个规格（如厚度 5mm、8+3）单独一组价格档位。客户报价时先匹配规格，再按最长边找档位。
        </p>
        {draft.specs.map((s, i) => (
          <div key={i} className="space-y-3 rounded-lg border bg-muted/20 p-3">
            <div className="flex flex-wrap items-end gap-2">
              <Field label="规格名称" hint="客户会直接说的叫法" className="min-w-[12rem] flex-1">
                <Input value={s.label} onChange={(e) => upd(i, { label: e.target.value })} placeholder="如：5mm 或 5+3" />
              </Field>
              <Field label="数值从(mm)" className="w-24">
                <Input value={s.min} onChange={(e) => upd(i, { min: e.target.value })} className="tabular-nums" />
              </Field>
              <Field label="到(mm)" className="w-24">
                <Input value={s.max} onChange={(e) => upd(i, { max: e.target.value })} className="tabular-nums" />
              </Field>
              <Button variant="ghost" size="icon" onClick={() => set({ specs: draft.specs.filter((_, j) => j !== i) })}
                disabled={draft.specs.length <= 1} aria-label="删除规格"
                className="text-muted-foreground hover:bg-danger-soft hover:text-danger-strong">
                <Trash2 />
              </Button>
            </div>
            <TierTable tiers={s.tiers} measure="length" onChange={(tiers) => upd(i, { tiers })} />
          </div>
        ))}
        <Button variant="outline" size="sm" icon={<Plus />}
          onClick={() => set({ specs: [...draft.specs, { label: "", min: "", max: "", tiers: [emptyTier("perCm")] }] })}>
          添加规格
        </Button>
      </div>
    );
  }
  return (
    <div className="space-y-2 text-sm">
      <p className="text-xs text-muted-foreground">
        {draft.measure === "area"
          ? "按整单总面积（㎡）匹配档位：面积落在哪一档，就按那一档的单价计算。"
          : "按单字最长边（cm）匹配档位。"}
      </p>
      <TierTable tiers={draft.tiers} measure={draft.measure} onChange={(tiers) => set({ tiers })} />
    </div>
  );
}

// ── ③ 附加费用 ───────────────────────────────────────────────────

// 放在组件外部：定义在渲染函数内会导致每次输入都重新挂载、输入框失焦
function Row({ label, hint, value, onChange }: {
  label: string; hint: string; value: string; onChange: (v: string) => void;
}) {
  return (
    <label className="grid items-center gap-x-3 gap-y-1 sm:grid-cols-[11rem_8rem_1fr]">
      <span className="font-medium text-foreground">{label}</span>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="留空 = 不启用" className="w-32 tabular-nums" />
      <span className="text-xs text-muted-foreground">{hint}</span>
    </label>
  );
}

function StepModifiers({ draft, set }: { draft: WizardDraft; set: (p: Partial<WizardDraft>) => void }) {
  const m = draft.modifiers;
  const upd = (patch: Partial<WizardDraft["modifiers"]>) => set({ modifiers: { ...m, ...patch } });

  return (
    <div className="space-y-3 text-sm">
      <p className="text-xs text-muted-foreground">都是可选项，留空表示该产品没有这项费用。</p>
      <Row label="最低起价（元）" hint="整单低于这个金额时按此金额收" value={m.minPrice} onChange={(v) => upd({ minPrice: v })} />
      {draft.measure === "area" && (
        <>
          <Row label="双面倍数" hint="客户要双面时价格乘以该倍数，如 1.6" value={m.doubleSided} onChange={(v) => upd({ doubleSided: v })} />
          <Row label="包边费（元/米）" hint="按包边长度加收" value={m.edgeFee} onChange={(v) => upd({ edgeFee: v })} />
          <Row label="裁切费（元/张）" hint="客户要求裁切时按张数加收" value={m.cuttingFee} onChange={(v) => upd({ cuttingFee: v })} />
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <label className="flex w-44 shrink-0 items-center gap-2 font-medium text-foreground">
              <input type="checkbox" className="h-4 w-4 accent-primary" checked={m.smallBatch} onChange={(e) => upd({ smallBatch: e.target.checked })} />
              <span>小批量特价</span>
            </label>
            {m.smallBatch && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">前</span>
                <Input value={m.smallBatchFirstN ?? "1"} onChange={(e) => upd({ smallBatchFirstN: e.target.value })}
                  aria-label="前 N 张" className="w-14 tabular-nums" />
                <span className="text-xs text-muted-foreground">张按档位价，后续每张</span>
                <Input value={m.smallBatchYuan} onChange={(e) => upd({ smallBatchYuan: e.target.value })}
                  aria-label="后续每张单价" className="w-20 tabular-nums" />
                <span className="text-xs text-muted-foreground">元（仅对固定价档位生效）</span>
              </div>
            )}
          </div>
        </>
      )}
      <PreservedModifiersNote draft={draft} />
      <div className="grid gap-3 pt-2">
        <ModifierSandbox modifiers={draftToModifiers(draft)} />
        <div><ModifierHelp /></div>
      </div>
    </div>
  );
}

/** 表单没有对应项的修饰项：只提示数量和类型名（不展示参数），保存时原样保留 */
function PreservedModifiersNote({ draft }: { draft: WizardDraft }) {
  const kept = preservedModifiers(draft.modifiers);
  if (!kept.length) return null;
  return (
    <Alert variant="warning" className="text-xs">
      该产品另有 {kept.length} 项向导不直接编辑的附加规则（
      {kept.map((k) => (k.is_active === 0 ? `${k.modifier_type}（已停用）` : k.modifier_type)).join("、")}），
      保存时会原样保留；已停用的规则不参与计价。
    </Alert>
  );
}

// ── ④ 确认预览（中文摘要，不展示任何 JSON）──────────────────────

function StepReview({ draft }: { draft: WizardDraft }) {
  const m = draft.modifiers;
  const modLines = useMemo(() => {
    const lines: string[] = [];
    if (m.minPrice.trim()) lines.push(`最低起价 ${m.minPrice} 元`);
    if (m.doubleSided.trim()) lines.push(`双面 ×${m.doubleSided}`);
    if (m.edgeFee.trim()) lines.push(`包边 ${m.edgeFee} 元/米`);
    if (m.cuttingFee.trim()) lines.push(`裁切 ${m.cuttingFee} 元/张`);
    if (m.smallBatch) lines.push(`小批量特价：前 ${m.smallBatchFirstN || 1} 张档位价，后续每张 ${m.smallBatchYuan || 1} 元`);
    return lines;
  }, [m]);

  return (
    <div className="space-y-3 text-sm">
      <dl className="grid gap-x-6 gap-y-2 rounded-lg border bg-muted/20 p-3 sm:grid-cols-2">
        <div><dt className="inline text-muted-foreground">产品名称：</dt><dd className="inline font-medium">{draft.name}</dd></div>
        <div><dt className="inline text-muted-foreground">标识：</dt><dd className="inline font-mono text-xs">{draft.key}</dd></div>
        <div><dt className="inline text-muted-foreground">计价方式：</dt><dd className="inline">{draft.measure === "area" ? "按面积（宽×高）" : "按尺寸（最长边）"}</dd></div>
        <div><dt className="inline text-muted-foreground">识别关键词：</dt><dd className="inline">{draft.keywords || "（无）"}</dd></div>
      </dl>

      <div>
        <div className="mb-1 font-medium text-foreground">价格档位</div>
        {draft.measure === "length" && draft.twoLevel ? (
          draft.specs.map((s, i) => (
            <div key={i} className="mb-2">
              <div className="text-xs font-medium text-primary">规格 {s.label}</div>
              <ul className="list-disc list-inside text-xs text-muted-foreground">
                {s.tiers.map((t, j) => <li key={j}>{tierLabel(t, "length")}</li>)}
              </ul>
            </div>
          ))
        ) : (
          <ul className="list-disc list-inside text-xs text-muted-foreground">
            {draft.tiers.map((t, j) => <li key={j}>{tierLabel(t, draft.measure)}</li>)}
          </ul>
        )}
      </div>

      <div>
        <div className="mb-1 font-medium text-foreground">附加费用</div>
        {modLines.length ? (
          <ul className="list-disc list-inside text-xs text-muted-foreground">
            {modLines.map((l, i) => <li key={i}>{l}</li>)}
          </ul>
        ) : (
          <div className="text-xs text-muted-foreground">无</div>
        )}
        <div className="mt-2"><PreservedModifiersNote draft={draft} /></div>
      </div>

      <p className="text-xs text-muted-foreground">保存后立即生效，客户下一次询价即按新价格计算。</p>
    </div>
  );
}
